import {
  useGetProducts,
  getGetProductsQueryOptions,
} from "@/requests/gen/react-query/product";
import {
  ProductRead,
  ProductTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import formatUSD from "@/lib/format-currency";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { Plus, Archive, FlaskConical } from "lucide-react";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { keepPreviousData } from "@tanstack/react-query";
import { resolveProductImage } from "@/lib/default-image";
import { Header } from "@/components/universal/header";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Toggle } from "@/components/ui/toggle";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { useQueryStates, type inferParserType } from "nuqs";

type ProductSearch = inferParserType<typeof dataTableSearchParsers>;

function productQueryParams(
  p: ProductSearch,
): Parameters<typeof getGetProductsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    product_types: p.product_types,
    entity_ids: p.entity_ids,
    is_deleted: p.is_deleted,
    only_samples: p.only_samples,
  };
}

export const Route = createFileRoute(
  "/_private/_patient-pharmcy-360/product/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetProductsQueryOptions(
        productQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: ProductLookup,
});

// Define the columns for the table
interface ProductTableProps {
  link?: LinkProps;
  image: string;
  name: string;
  product_type: string;
  price: number;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<ProductTableProps>[] = [
  {
    header: "",
    accessorKey: "image",
    cell: ({ row }) => (
      <img
        src={row.original.image}
        alt="Product"
        className="h-12 w-12 rounded-sm object-contain"
      />
    ),
  },
  {
    header: "Name",
    accessorKey: "name",
  },

  {
    header: "Type",
    accessorKey: "product_type",
    cell: (value) => (
      <Badge
        variant={
          value.getValue() === ProductTypeEnum.RX ? "default" : "secondary"
        }
      >
        {value.getValue() as string}
      </Badge>
    ),
  },
  { header: "NDC Code", accessorKey: "ndc_code" },
  {
    header: "Price",
    accessorKey: "price",
    cell: (value) => {
      return formatUSD(value.getValue() as number);
    },
  },
  {
    header: "Entity",
    accessorKey: "entity",
  },
];

// Main component
function ProductLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);
  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const onlyEntity = useOnlyEntity();

  // Fetch entities for the dropdown filter
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const products = useGetProducts(productQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  // Map the data to the table format
  const tableData: ProductTableProps[] | undefined = products.data?.items.map(
    (product: ProductRead) => {
      return {
        link: linkOptions({
          to: "/product/$productId",
          params: { productId: String(product.product_id) },
        }),
        image: resolveProductImage(product.catalog_product),
        name: `${product.catalog_product.is_sample ? "SAMPLE - " : ""}${product.display_name}`,
        product_type: product.catalog_product.product_base.product_type,
        ndc_code: product.catalog_product.ndc_code,
        price: product.entity_price || 0,
        entity: product.entity.name,
      };
    },
  );

  return (
    <div>
      <title>Products - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Products", link: { to: "/product/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>{queryStates.is_deleted && "Archived "}Products</CardTitle>
          <CardDescription>
            View your products below. You can search, filter, and click to see
            more details.
          </CardDescription>
          {!queryStates.is_deleted && (
            <CardAction>
              <Link to="/product/create">
                <Button variant="default" size="sm">
                  <Plus />
                  Add Product
                </Button>
              </Link>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={products.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((product_type) => {
                    return { label: product_type, value: product_type };
                  }),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            {!onlyEntity && (
              <DropdownFilter
                title="Entity"
                options={[
                  {
                    items:
                      entities.data?.pages
                        .flatMap((page) => page.items)
                        .map((entity) => {
                          return {
                            label: entity.name,
                            value: entity.entity_id,
                          };
                        }) ?? [],
                  },
                ]}
                values={queryStates.entity_ids}
                onChange={(values) => setQueryStates({ entity_ids: values })}
                search={entitySearch}
                setSearch={setEntitySearch}
                isFetching={entities.isFetchingNextPage}
                fetchNextPage={entities.fetchNextPage}
              />
            )}
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) =>
                setQueryStates({ is_deleted: pressed })
              }
            >
              <Archive />
              Archived
            </Toggle>
            <Toggle
              pressed={queryStates.only_samples}
              onPressedChange={(pressed) =>
                setQueryStates({ only_samples: pressed })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={products.isLoading}
            isError={products.isError}
            count={products.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
